import React from 'react';
import I18n from 'i18n-js';
import { sortBy } from 'lodash';

import { inMobileOrTabletBrowser } from '@reverbdotcom/commons/src/user_context_helpers';
import { RCSelect, RCCombobox } from '@reverbdotcom/cadence/components';
import { conditionSlugForUuid, conditionUuidForSlug, PGT_CSP_MODULE_CONDITIONS } from '@reverbdotcom/commons/src/condition_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

import V2CSPPriceGuideModuleContent from './v2_csp_price_guide_module_content';

import { CSP } from '../../../../schemas/rql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

const DEFAULT_MODEL_VALUE = 'all-models';
export const DEFAULT_MODEL = {
  value: DEFAULT_MODEL_VALUE,
  label: I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.defaultModelDisplay'),
};

const DEFAULT_CONDITION_VALUE = 'good-to-mint';
export const DEFAULT_CONDITION = {
  value: DEFAULT_CONDITION_VALUE,
  label: I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.defaultConditionDisplay'),
};
interface IExternalProps {
  csp: CSP;
}

function hasMultipleModels(variants) {
  return variants.length > 1;
}

function defaultModelValue(variants) {
  return hasMultipleModels(variants) ? DEFAULT_MODEL.value : variants[0]?.id;
}

export function ModelHelpText() {
  return (
    <div className="mt-2">
      <p>
        {I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.modelHelpText')}
      </p>
    </div>
  );
}

export function modelFilterOptions(variants) {
  const sortedVariants = sortBy(variants, variant => variant.name);

  const options = hasMultipleModels(sortedVariants) ? [DEFAULT_MODEL] : [];

  sortedVariants.forEach(variant => { 
    options.push({
      value: variant.id, 
      label: variant.name,
    });
  });

  return options;
}

export function conditionFilterOptions(conditionSlugs) {
  const options = [DEFAULT_CONDITION];

  conditionSlugs.forEach(slug => { 
    options.push({
      value: conditionUuidForSlug(slug),
      label: I18n.t(`discovery.conditions.${slug}`),
    });
  });

  return options;
}

function trackFiltering({ csp, canonicalProductId, conditionUuid }) {
  const cpId = canonicalProductId == DEFAULT_MODEL.value ? null : canonicalProductId;
  const conditionSlug = conditionUuid == DEFAULT_CONDITION.value ? null : conditionSlugForUuid(conditionUuid);

  trackEvent({
    eventName: MParticleEventName.FilteringOnCSPPriceGuideModule,
    componentName: 'V2CSPPriceGuideModuleLayout',
    cspId: csp.id,
    cpId,
    conditionSlug,
  });
}

export default function V2CSPPriceGuideModuleLayout({ 
  csp,
}: IExternalProps) {
  const user = useUser();
  
  const { variants = [] } = csp;

  const [selectedModelId, selectModelId] = React.useState(defaultModelValue(variants));
  const [selectedConditionUuid, selectConditionUuid] = React.useState(DEFAULT_CONDITION.value);

  const hasSelectedCondition = selectedConditionUuid != DEFAULT_CONDITION.value;
  const hasSelectedModel = selectedModelId != DEFAULT_MODEL.value;

  const defaultConditionUuids = PGT_CSP_MODULE_CONDITIONS.map(slug => conditionUuidForSlug(slug));
  const conditionOptions = conditionFilterOptions(PGT_CSP_MODULE_CONDITIONS);
  const variantOptions = modelFilterOptions(variants);

  return (
    <div className="v2-csp-price-guide-module-layout">
      <div className="v2-csp-price-guide-module-layout__filters-container">
        <div className="v2-csp-price-guide-module-layout__filters-container__title">
          {I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.title', {
            cspTitle: csp.title,
          })}
        </div>

        <div className="v2-csp-price-guide-module-layout__filters-container__filters">
          <div className="v2-csp-price-guide-module-layout_filters_container__filters__model-filter">
            {inMobileOrTabletBrowser(user) && (
              <RCSelect
                id="cspPriceGuideModelVersion"
                name="cspPriceGuideModelVersion"
                label={I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.modelFilterLabel')}
                onChange={event => {
                  const newModelId = event.target.value;

                  trackFiltering({
                    csp,
                    canonicalProductId: newModelId,
                    conditionUuid: selectedConditionUuid,
                  });

                  selectModelId(newModelId);
                }}
                value={selectedModelId}
                helpText={ModelHelpText()}
              >
                {variantOptions.map(variant => (
                  <RCSelect.Option
                    key={`variant-${variant.value}`}
                    value={variant.value}
                    label={variant.label}
                  />
                ))}
              </RCSelect>
            )}

            {!inMobileOrTabletBrowser(user) && (
              <RCCombobox
                id="cspPriceGuideModelVersion"
                name="cspPriceGuideModelVersion"
                label={I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.modelFilterLabel')}
                options={variantOptions}
                onOptionSelect={event => {
                  const newModelId = event.value;

                  trackFiltering({
                    csp,
                    canonicalProductId: newModelId,
                    conditionUuid: selectedConditionUuid,
                  });

                  selectModelId(newModelId);
                }}
                value={selectedModelId}
                helpText={ModelHelpText()}
              />
            )} 
          </div>

          <div className="v2-csp-price-guide-module-layout_filters_container__filters__condition-filter">
            <RCSelect
              id="cspPriceGuideCondition"
              name="cspPriceGuideCondition"
              label={I18n.t('discovery.priceGuideTool.cspModule.v2Layout.filterHeader.conditionFilterLabel')}
              onChange={event => {
                const newConditionUuid = event.target.value;

                trackFiltering({
                  csp,
                  canonicalProductId: selectedModelId,
                  conditionUuid: newConditionUuid,
                });

                selectConditionUuid(newConditionUuid);
              }}
              value={selectedConditionUuid}
            >
              {conditionOptions.map(condition => (
                <RCSelect.Option
                  key={`condition-${condition.value}`}
                  value={condition.value}
                  label={condition.label}
                />
              ))}
            </RCSelect>
          </div>
        </div>
      </div>

      <V2CSPPriceGuideModuleContent
        csp={csp}
        conditionUuids={hasSelectedCondition ? [selectedConditionUuid] : defaultConditionUuids}
        canonicalProductId={hasSelectedModel ? selectedModelId : null}
      />
    </div>
  );
}
